import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Badge,
  Avatar,
  TextField,
  InputAdornment,
  Button,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  CircularProgress,
  Tooltip,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ChatIcon from "@mui/icons-material/Chat";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ForumIcon from "@mui/icons-material/Forum";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useAuth } from "../../firebase/authContext";
import {
  d1SearchUsers,
  d1GetFriends,
  d1GetFriendRequests,
  d1SendFriendRequest,
  d1RespondFriendRequest,
  d1RemoveFriend,
  d1GetConversations,
  d1GetMessages,
  d1SendMessage,
  d1MarkMessagesRead,
} from "../../services/d1Client";
import {
  getCanonicalLastRead,
  getCurrentReaderChapter,
} from "../../bible/readingHistoryStorage";
import {
  extractVerseMap,
  formatVerseQuote,
  verseQuoteReference,
  maskEmail,
  resolveQuoteChapter,
  sanitizeQuoteDisplay,
} from "../../bible/verseQuote";
import {
  BOOKS_ZH,
  buildChapterId,
  getCanonicalChapterCount,
  getLocalizedBookName,
  resolveBookIndex,
} from "../../bible/bookNames";
import { getReaderVerseFocus, toPositiveVerse } from "../../bible/readerVerseFocus";
import { normalizeReadingUid } from "../../bible/readingStorageScope";
import QuotePickerDialog, { verseSelectOptions } from "./QuotePickerDialog";
import {
  formatMessageDayLabel,
  formatMessageTime,
  getMessageTimestamp,
  isEnglishPath,
  isSameCalendarDay,
} from "../../bible/messageTime";

const QUICK_BLESSINGS = [
  "🙏 祷告记念",
  "✝️ 主恩同在",
  "📖 愿神赐福",
  "❤️ 互为肢体",
  "🌸 平安喜乐",
];

const findChapterById = (chapters, chapterId) =>
  (chapters || []).find((ch) => ch.id === chapterId) || null;

export const pickQuoteDraftVerse = (requested, verseNums) => {
  const wanted = toPositiveVerse(requested);
  if (!verseNums.length) return wanted || 1;
  if (wanted && verseNums.includes(wanted)) return wanted;
  return verseNums[0];
};

export const mergeLoadedQuoteDraft = (prev, loaded) => {
  const wanted = toPositiveVerse(prev?.verse) || toPositiveVerse(loaded.verse);
  const verse = pickQuoteDraftVerse(wanted, loaded.verseNums);
  return {
    ...loaded,
    verse,
    verseText: loaded.verseMap?.[verse] || loaded.verseText || "",
  };
};

const buildQuoteDraft = ({
  chapter,
  chapterId,
  bookName,
  chapterNumber,
  verse,
  bookIndex,
}) => {
  const verseMap = extractVerseMap(chapter?.content);
  const verseNums = Object.keys(verseMap)
    .map(Number)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  const requested = toPositiveVerse(verse);
  const selected = pickQuoteDraftVerse(requested, verseNums);
  const nums = verseSelectOptions(verseNums, selected);
  const localizedBook = getLocalizedBookName(
    {
      chapterId,
      bookName: chapter?.bookName || bookName,
    },
    false
  );
  return {
    chapterId,
    bookName: localizedBook || "圣经",
    chapterNumber: chapter?.chapterNumber || chapterNumber || 1,
    verse: selected,
    verseText: verseMap[selected] || "",
    verseMap,
    verseNums: nums,
    bookIndex:
      bookIndex ||
      resolveBookIndex({
        chapterId,
        bookName: localizedBook || bookName,
      }),
  };
};

export default function SocialModal({
  open,
  onClose,
  initialTab = 0,
  initialTargetUser = null,
  onOpenUserCard,
  onUnreadChange,
}) {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState(initialTab); // 0: Chat, 1: Friends
  const [conversations, setConversations] = useState([]);
  const [conversationsReady, setConversationsReady] = useState(false);
  const [activeChatUser, setActiveChatUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [attachedVerse, setAttachedVerse] = useState(null);
  const [sending, setSending] = useState(false);

  // Friends state
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [actionNotice, setActionNotice] = useState("");
  const [respondingId, setRespondingId] = useState(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteDraft, setQuoteDraft] = useState({
    chapterId: "",
    bookName: "",
    chapterNumber: 1,
    verse: 1,
    verseText: "",
    verseMap: {},
    verseNums: [1],
    bookIndex: null,
  });
  const dialogPaperRef = useRef(null);

  const isEn =
    typeof window !== "undefined" && isEnglishPath(window.location.pathname);

  const messagesEndRef = useRef(null);
  const pollTimerRef = useRef(null);
  const appliedTargetRef = useRef(null);
  const activeChatUidRef = useRef(null);
  const attachedVerseRef = useRef(null);
  const bibleChaptersCacheRef = useRef({ zh: null, en: null });
  const currentUid = currentUser?.uid;

  const setPendingQuote = (value) => {
    attachedVerseRef.current = value || null;
    setAttachedVerse(value || null);
  };

  // Auto scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load friends and pending requests
  const loadFriendsData = useCallback(async () => {
    if (!currentUser) return;
    try {
      const [fList, reqs] = await Promise.all([
        d1GetFriends(),
        d1GetFriendRequests(),
      ]);
      const seenUid = activeChatUidRef.current;
      setFriends(
        (fList || []).map((f) =>
          seenUid && f.uid === seenUid ? { ...f, unreadMessages: 0 } : f
        )
      );
      setFriendRequests(reqs || { incoming: [], outgoing: [] });
    } catch (e) {
      console.warn("Error loading friends:", e);
    }
  }, [currentUser]);

  // Load conversations list
  const loadConversations = useCallback(async () => {
    if (!currentUser) {
      setConversationsReady(true);
      return;
    }
    try {
      const list = await d1GetConversations();
      const seenUid = activeChatUidRef.current;
      setConversations(
        (list || []).map((c) =>
          seenUid && c.otherUser?.uid === seenUid ? { ...c, unreadCount: 0 } : c
        )
      );
    } catch (e) {
      console.warn("Error loading conversations:", e);
    } finally {
      setConversationsReady(true);
    }
  }, [currentUser]);

  const markConversationSeen = useCallback(
    (otherUid) => {
      if (!otherUid) return 0;
      let cleared = 0;
      setConversations((prev) =>
        prev.map((c) => {
          if (c.otherUser?.uid !== otherUid) return c;
          cleared += c.unreadCount || 0;
          return { ...c, unreadCount: 0 };
        })
      );
      setFriends((prev) =>
        prev.map((f) => (f.uid === otherUid ? { ...f, unreadMessages: 0 } : f))
      );
      if (onUnreadChange) {
        onUnreadChange({ clearedMessages: cleared, otherUid });
      }
      return cleared;
    },
    [onUnreadChange]
  );

  // Load messages for active conversation
  const loadActiveMessages = useCallback(async (otherUid, isBackground = false) => {
    if (!otherUid || !currentUser) return;
    if (!isBackground) {
      setLoadingMessages(true);
      markConversationSeen(otherUid);
    }
    try {
      const data = await d1GetMessages(otherUid);
      setMessages(data?.messages || []);
      if (!isBackground) {
        try {
          await d1MarkMessagesRead(otherUid);
        } catch (e) {
          // GET already marks incoming rows read; this is a second explicit pass
        }
        markConversationSeen(otherUid);
        setTimeout(scrollToBottom, 100);
        loadConversations();
        loadFriendsData();
      } else if (onUnreadChange) {
        onUnreadChange();
      }
    } catch (e) {
      console.warn("Error loading chat messages:", e);
    } finally {
      if (!isBackground) setLoadingMessages(false);
    }
  }, [currentUser, onUnreadChange, loadConversations, loadFriendsData, markConversationSeen]);

  useEffect(() => {
    setConversations([]);
    setConversationsReady(false);
    setActiveChatUser(null);
    activeChatUidRef.current = null;
    setMessages([]);
    setMessageInput("");
    setPendingQuote(null);
    setFriends([]);
    setFriendRequests({ incoming: [], outgoing: [] });
    setSearchQuery("");
    setSearchResults([]);
    setActionNotice("");
    setRespondingId(null);
    appliedTargetRef.current = null;
    setQuoteDialogOpen(false);
    setQuoteDraft({
      chapterId: "",
      bookName: "",
      chapterNumber: 1,
      verse: 1,
      verseText: "",
      verseMap: {},
      verseNums: [1],
      bookIndex: null,
    });
  }, [currentUid]);

  useEffect(() => {
    if (!actionNotice) return undefined;
    const timer = window.setTimeout(() => setActionNotice(""), 2500);
    return () => window.clearTimeout(timer);
  }, [actionNotice]);

  // Apply a chat target only once per open so loader refreshes cannot clobber the peer.
  useEffect(() => {
    if (!open) {
      appliedTargetRef.current = null;
      setConversationsReady(false);
      return;
    }
    if (!currentUser) return;

    loadFriendsData();
    loadConversations();
    if (initialTab !== undefined) {
      setActiveTab(initialTab);
    }

    const targetUid = initialTargetUser?.uid;
    if (!targetUid || appliedTargetRef.current === targetUid) return;

    if (targetUid === currentUser.uid) {
      appliedTargetRef.current = targetUid;
      setActionNotice("不能给自己发私信，请选择对方的对话");
      return;
    }

    appliedTargetRef.current = targetUid;
    setActiveTab(0);
    activeChatUidRef.current = targetUid;
    setActiveChatUser(initialTargetUser);
    if (initialTargetUser.verseQuote) {
      setPendingQuote(initialTargetUser.verseQuote);
    }
    loadActiveMessages(targetUid);
  }, [open, currentUser, initialTab, initialTargetUser, loadFriendsData, loadConversations, loadActiveMessages]);

  // Polling for active conversation (every 4s)
  useEffect(() => {
    if (open && activeTab === 0 && activeChatUser?.uid) {
      pollTimerRef.current = setInterval(() => {
        loadActiveMessages(activeChatUser.uid, true);
        loadConversations();
      }, 4000);
      return () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      };
    }
  }, [open, activeTab, activeChatUser, loadActiveMessages, loadConversations]);

  const handleStartChatWithUser = (user) => {
    if (!user?.uid) return;
    if (user.uid === currentUser?.uid) {
      setActionNotice("不能给自己发私信，请选择对方的对话");
      return;
    }
    setActiveTab(0);
    activeChatUidRef.current = user.uid;
    setActiveChatUser(user);
    markConversationSeen(user.uid);
    loadActiveMessages(user.uid);
  };

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && !attachedVerse) || !activeChatUser || sending) return;
    if (activeChatUser.uid === currentUser?.uid) {
      setActionNotice("不能给自己发私信，请选择对方的对话");
      return;
    }

    const verseQuote = attachedVerseRef.current || attachedVerse;
    const content = messageInput.trim() || (verseQuote ? `📖 [经文分享] ${verseQuote}` : "");
    if (content.length > 2000) {
      setActionNotice("消息过长，请控制在 2000 字以内");
      return;
    }
    setSending(true);

    try {
      const sentMsg = await d1SendMessage(activeChatUser.uid, content, verseQuote);
      if (!sentMsg?.id) {
        setActionNotice("发送失败，对方可能未收到，请重试");
        return;
      }
      setMessages((prev) => [
        ...prev,
        {
          ...sentMsg,
          verseQuote: sentMsg.verseQuote || verseQuote || null,
        },
      ]);
      setMessageInput("");
      setPendingQuote(null);
      setTimeout(scrollToBottom, 50);
      loadConversations();
    } catch (e) {
      console.error("Failed to send message:", e);
      setActionNotice(e.message || "发送失败，对方可能未收到，请重试");
    } finally {
      setSending(false);
    }
  };

  const guessQuoteEdition = useCallback((source = {}) => {
    const uid = normalizeReadingUid(currentUid);
    const lastRead = uid ? getCanonicalLastRead(uid) : null;
    const loc = typeof window !== "undefined" ? window.location : { pathname: "" };
    return (
      source.editionId ||
      lastRead?.editionId ||
      (String(loc.pathname || "").includes("/en/") ? "en" : "zh")
    );
  }, [currentUid]);

  const loadBibleChapters = useCallback(async (editionGuess) => {
    const key = editionGuess === "en" ? "en" : "zh";
    if (bibleChaptersCacheRef.current[key]) {
      return bibleChaptersCacheRef.current[key];
    }
    try {
      const mod =
        key === "en"
          ? await import("../../data/bibleDataEn")
          : await import("../../data/bibleData");
      const chapters = mod.bibleChapters || [];
      bibleChaptersCacheRef.current[key] = chapters;
      return chapters;
    } catch (e) {
      return [];
    }
  }, []);

  const loadQuoteDraft = useCallback(
    async ({ chapterId, bookName, chapterNumber, verse, bookIndex, editionGuess }) => {
      const edition = editionGuess || guessQuoteEdition();
      let chapters = await loadBibleChapters(edition);
      let chapter = chapterId ? findChapterById(chapters, chapterId) : null;
      if (!chapter && edition !== "en" && chapterId) {
        chapters = await loadBibleChapters("en");
        chapter = findChapterById(chapters, chapterId);
      }
      return buildQuoteDraft({
        chapter,
        chapterId,
        bookName,
        chapterNumber,
        verse,
        bookIndex,
      });
    },
    [guessQuoteEdition, loadBibleChapters]
  );

  const releaseDialogFocus = () => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active === document.body) return;
    const paper = dialogPaperRef.current;
    if (!paper || paper.contains(active)) {
      active.blur();
    }
  };

  const handleDialogClose = () => {
    setQuoteDialogOpen(false);
    releaseDialogFocus();
    onClose();
  };

  const handleAttachCurrentReading = async () => {
    // Wait for a real uid so we never read unscoped leftovers from the
    // previous account during the logout → login gap (no page reload).
    const uid = normalizeReadingUid(currentUid);
    const lastRead = uid ? getCanonicalLastRead(uid) : null;
    const currentReader = uid ? getCurrentReaderChapter(uid) : null;
    const readerVerse = uid ? getReaderVerseFocus(undefined, uid) : null;
    const loc = typeof window !== "undefined" ? window.location : { pathname: "", search: "" };
    const source = resolveQuoteChapter({
      pathname: loc.pathname,
      search: loc.search,
      lastRead,
      currentReader,
      readerVerse,
    });
    const chapterId = source.chapterId || lastRead?.chapterId || "";
    const seedArgs = {
      chapterId,
      bookName: source.bookName || lastRead?.bookName,
      chapterNumber: source.chapterNumber || lastRead?.chapterNumber || 1,
      verse: source.verse,
      editionGuess: guessQuoteEdition(source),
    };
    const seed = buildQuoteDraft(seedArgs);
    setQuoteDraft(seed);
    setQuoteDialogOpen(true);
    const loaded = await loadQuoteDraft(seedArgs);
    setQuoteDraft((prev) => {
      const samePlace =
        prev.chapterId === seed.chapterId &&
        Number(prev.bookIndex) === Number(seed.bookIndex) &&
        Number(prev.chapterNumber) === Number(seed.chapterNumber);
      if (!samePlace) return prev;
      return mergeLoadedQuoteDraft(prev, loaded);
    });
  };

  const handleQuoteLocationChange = async ({ bookIndex, chapterNumber, verse } = {}) => {
    const nextBook = Number(bookIndex || quoteDraft.bookIndex) || 1;
    const maxChapter = getCanonicalChapterCount(nextBook);
    let nextChapter = Number(chapterNumber || quoteDraft.chapterNumber) || 1;
    if (nextChapter > maxChapter) nextChapter = 1;
    const nextVerse = verse != null ? Number(verse) : 1;
    const chapterId = buildChapterId(nextBook, nextChapter);
    const draft = await loadQuoteDraft({
      chapterId,
      bookName: BOOKS_ZH[nextBook - 1],
      chapterNumber: nextChapter,
      verse: nextVerse,
      bookIndex: nextBook,
    });
    setQuoteDraft(draft);
  };

  const handleConfirmQuote = () => {
    setPendingQuote(
      formatVerseQuote({
        bookName: quoteDraft.bookName,
        chapterNumber: quoteDraft.chapterNumber,
        verse: quoteDraft.verse,
        verseText: quoteDraft.verseText,
      })
    );
    setQuoteDialogOpen(false);
  };

  // Handle friend search
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim() || searching) return;
    setSearching(true);
    try {
      const results = await d1SearchUsers(searchQuery);
      setSearchResults(results || []);
      if ((results || []).length === 0) {
        setActionNotice("未找到匹配的读者");
      }
    } catch (e) {
      setActionNotice("搜索失败，请稍后重试");
    } finally {
      setSearching(false);
    }
  };

  // Handle send friend request
  const handleSendRequest = async (targetUid) => {
    try {
      const res = await d1SendFriendRequest(targetUid);
      if (res && res.status === "accepted") {
        setActionNotice("对方此前已向您发送申请，现已直接结为主内好友！");
      } else {
        setActionNotice("好友申请已发送，等待对方验证通过！");
      }
      loadFriendsData();
      if (searchQuery.trim()) {
        handleSearch();
      }
    } catch (e) {
      setActionNotice(e.message || "发送申请失败");
    }
  };

  // Handle respond to incoming friend request
  const handleRespondRequest = async (requestId, action) => {
    if (respondingId) return;
    setRespondingId(requestId);
    try {
      await d1RespondFriendRequest(requestId, action);
      setActionNotice(action === "accept" ? "已同意好友申请，已添加到好友列表！" : "已忽略该申请");
      loadFriendsData();
      if (onUnreadChange) onUnreadChange();
    } catch (e) {
      setActionNotice("处理申请失败");
    } finally {
      setRespondingId(null);
    }
  };

  // Handle remove friend
  const handleRemoveFriend = async (targetUid) => {
    if (!window.confirm("确定要解除好友关系吗？")) return;
    try {
      await d1RemoveFriend(targetUid);
      setActionNotice("已解除好友关系");
      loadFriendsData();
      loadConversations();
    } catch (e) {
      setActionNotice("解除好友失败");
    }
  };

  // Open user profile popover card
  const handleAvatarClick = (e, user) => {
    e.stopPropagation();
    if (onOpenUserCard) {
      onOpenUserCard(user);
    }
  };

  const incomingCount = friendRequests.incoming.length;
  const totalUnreadMessages = conversations.reduce((acc, c) => acc + (c.unreadCount || 0), 0);

  return (
    <>
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onExit: releaseDialogFocus,
      }}
      PaperProps={{
        ref: dialogPaperRef,
        sx: {
          borderRadius: { xs: 2.5, sm: 3.5 },
          height: { xs: "94vh", sm: "660px" },
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        },
      }}
    >
      {/* Sleek Top Navigation Header */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "background.paper" : "#ffffff",
          zIndex: 2,
        }}
      >
        {/* Left: Spiritual Brand Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
            }}
          >
            <ForumIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              主内团契
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
              灵修对话 · 肢体相交
            </Typography>
          </Box>
        </Box>

        {/* Center: Segmented Pill Tab Controls */}
        <Box
          sx={{
            display: "flex",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
            borderRadius: "20px",
            p: 0.5,
            gap: 0.5,
          }}
        >
          {/* Tab 0: Chat */}
          <Button
            size="small"
            onClick={() => {
              setActiveTab(0);
              setActionNotice("");
            }}
            startIcon={
              <Badge badgeContent={totalUnreadMessages} color="error" max={99}>
                <ChatIcon sx={{ fontSize: 18 }} />
              </Badge>
            }
            sx={{
              borderRadius: "16px",
              px: { xs: 1.5, sm: 2 },
              py: 0.6,
              fontSize: "0.82rem",
              fontWeight: 800,
              textTransform: "none",
              color: activeTab === 0 ? "#ffffff" : "text.secondary",
              background:
                activeTab === 0
                  ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                  : "transparent",
              boxShadow: activeTab === 0 ? "0 2px 8px rgba(25, 118, 210, 0.3)" : "none",
              "&:hover": {
                background:
                  activeTab === 0
                    ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                    : "rgba(0,0,0,0.04)",
              },
            }}
          >
            {isMobile ? "私信" : "私信交流"}
          </Button>

          {/* Tab 1: Friends */}
          <Button
            size="small"
            onClick={() => {
              setActiveTab(1);
              setActionNotice("");
            }}
            startIcon={
              <Badge badgeContent={incomingCount} color="error" max={99}>
                <PeopleIcon sx={{ fontSize: 18 }} />
              </Badge>
            }
            sx={{
              borderRadius: "16px",
              px: { xs: 1.5, sm: 2 },
              py: 0.6,
              fontSize: "0.82rem",
              fontWeight: 800,
              textTransform: "none",
              color: activeTab === 1 ? "#ffffff" : "text.secondary",
              background:
                activeTab === 1
                  ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                  : "transparent",
              boxShadow: activeTab === 1 ? "0 2px 8px rgba(25, 118, 210, 0.3)" : "none",
              "&:hover": {
                background:
                  activeTab === 1
                    ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                    : "rgba(0,0,0,0.04)",
              },
            }}
          >
            {isMobile ? "好友" : "主内好友"}
            {friends.length > 0 && ` (${friends.length})`}
          </Button>
        </Box>

        {/* Right: Close Button */}
        <IconButton
          onClick={handleDialogClose}
          size="small"
          aria-label="关闭"
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f1f5f9",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Main Content Area */}
      <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", overflow: "hidden", position: "relative" }}>
        {actionNotice && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 16,
              zIndex: 100,
              maxWidth: 360,
            }}
          >
            <Alert
              severity="info"
              onClose={() => setActionNotice("")}
              sx={{
                borderRadius: 2.5,
                boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
                fontSize: "0.82rem",
              }}
            >
              {actionNotice}
            </Alert>
          </Box>
        )}

        {/* ======================================================== */}
        {/* TAB 0: CHAT / DIRECT MESSAGES                            */}
        {/* ======================================================== */}
        {activeTab === 0 && (
          <Box sx={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>
            {/* Sidebar: Conversations List */}
            {(!isMobile || !activeChatUser) && (
              <Box
                sx={{
                  width: { xs: "100%", sm: "280px", md: "310px" },
                  height: "100%",
                  borderRight: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "background.default" : "#fafbfc",
                  flexShrink: 0,
                }}
              >
                {/* Sidebar Header */}
                <Box
                  sx={{
                    p: 1.5,
                    px: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    对话消息
                  </Typography>
                  <Tooltip title="刷新对话列表">
                    <IconButton size="small" onClick={loadConversations}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Conversations Scroll List */}
                <List sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
                  {!conversationsReady ? (
                    <Box
                      data-testid="conversations-loading"
                      sx={{ p: 3, textAlign: "center", my: "auto" }}
                      aria-busy="true"
                      aria-label="正在加载对话"
                    >
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        正在加载对话...
                      </Typography>
                    </Box>
                  ) : conversations.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center", my: "auto" }}>
                      <ChatIcon sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        暂无私信对话
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setActiveTab(1)}
                        sx={{ mt: 1.5, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                      >
                        从好友列表发起聊天
                      </Button>
                    </Box>
                  ) : (
                    conversations.map((c) => {
                      const isSelected = activeChatUser?.uid === c.otherUser.uid;
                      const initial = (c.otherUser.displayName || "读")[0].toUpperCase();
                      const unread = c.unreadCount || 0;

                      return (
                        <ListItem
                          key={c.conversationId}
                          button
                          selected={isSelected}
                          onClick={() => handleStartChatWithUser(c.otherUser)}
                          sx={{
                            borderRadius: 2.5,
                            mb: 0.5,
                            py: 1,
                            px: 1.5,
                            transition: "all 0.15s ease",
                            border: isSelected ? "1px solid" : "1px solid transparent",
                            borderColor: isSelected ? "primary.main" : "transparent",
                            bgcolor: isSelected
                              ? (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(25, 118, 210, 0.2)"
                                    : "rgba(25, 118, 210, 0.08)"
                              : "transparent",
                            "&:hover": {
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.03)",
                            },
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 46 }}>
                            <Badge badgeContent={unread} color="error" max={99}>
                              <Avatar
                                src={c.otherUser.photoURL}
                                onClick={(e) => handleAvatarClick(e, c.otherUser)}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: "primary.main",
                                  fontWeight: 800,
                                  fontSize: "1rem",
                                  cursor: "pointer",
                                  transition: "transform 0.15s ease",
                                  "&:hover": { transform: "scale(1.08)" },
                                }}
                              >
                                {initial}
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: "0.88rem" }} noWrap>
                                  {c.otherUser.displayName || "主内读者"}
                                </Typography>
                                {getMessageTimestamp(c.lastMessage) && (
                                  <Typography
                                    data-testid="conversation-time"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.65rem", ml: 1, flexShrink: 0, whiteSpace: "nowrap" }}
                                  >
                                    {formatMessageTime(getMessageTimestamp(c.lastMessage), { isEn })}
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mt: 0.25 }}>
                                {c.lastMessage?.verseQuote ? `📖 [经文] ` : ""}
                                {c.lastMessage?.content || "点击开始交流"}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Box>
            )}

            {/* Active Conversation Pane */}
            {(!isMobile || activeChatUser) && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: (theme) => theme.palette.background.paper,
                  overflow: "hidden",
                }}
              >
                {activeChatUser ? (
                  <>
                    {/* Chat Header */}
                    <Box
                      sx={{
                        p: { xs: 1, sm: 1.25 },
                        px: { xs: 1.25, sm: 2 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "background.default" : "#ffffff",
                        flexShrink: 0,
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                        {isMobile && (
                          <IconButton
                            size="small"
                            onClick={() => setActiveChatUser(null)}
                            sx={{ mr: -0.5, flexShrink: 0 }}
                          >
                            <ArrowBackIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Avatar
                          src={activeChatUser.photoURL}
                          onClick={(e) => handleAvatarClick(e, activeChatUser)}
                          sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            bgcolor: "primary.main",
                            fontWeight: 800,
                            cursor: "pointer",
                            transition: "transform 0.15s ease",
                            flexShrink: 0,
                            "&:hover": { transform: "scale(1.08)" },
                          }}
                        >
                          {(activeChatUser.displayName || "读")[0].toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ fontWeight: 800, lineHeight: 1.2 }}
                          >
                            {activeChatUser.displayName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ fontSize: "0.68rem", display: { xs: "none", sm: "block" } }}
                          >
                            主内同行 · 私信对话
                          </Typography>
                        </Box>
                      </Box>

                      {/* Header Actions */}
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={!isMobile ? <ContactPageIcon fontSize="small" /> : null}
                          onClick={(e) => handleAvatarClick(e, activeChatUser)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "0.75rem",
                            py: 0.4,
                            minWidth: 0,
                            whiteSpace: "nowrap",
                            wordBreak: "keep-all",
                            flexShrink: 0,
                          }}
                        >
                          名片
                        </Button>
                        <Tooltip title="刷新消息">
                          <IconButton
                            size="small"
                            onClick={() => loadActiveMessages(activeChatUser.uid)}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Messages Scroll Area */}
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        p: { xs: 1.5, sm: 2.5 },
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "#121417" : "#f8fafc",
                      }}
                    >
                      {loadingMessages ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : messages.length === 0 ? (
                        <Box sx={{ textAlign: "center", my: "auto", py: 4, px: 2 }}>
                          <AutoAwesomeIcon sx={{ fontSize: 36, color: "primary.main", opacity: 0.6, mb: 1 }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            愿主赐你们彼此相交的恩典
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            发送一条私信或经文，开启与 {activeChatUser.displayName} 的灵修交流吧！
                          </Typography>
                        </Box>
                      ) : (
                        messages.map((m, index) => {
                          const isMe = m.senderId === currentUser?.uid;
                          const senderAvatar = isMe ? currentUser.photoURL : activeChatUser.photoURL;
                          const senderInitial = isMe
                            ? (currentUser.displayName || "我")[0].toUpperCase()
                            : (activeChatUser.displayName || "读")[0].toUpperCase();
                          const createdAt = getMessageTimestamp(m);
                          const prevCreatedAt = getMessageTimestamp(messages[index - 1]);
                          const showDaySeparator =
                            Boolean(createdAt) &&
                            (index === 0 || !isSameCalendarDay(prevCreatedAt, createdAt));

                          return (
                            <React.Fragment key={m.id}>
                            {showDaySeparator && (
                              <Box
                                data-testid="message-day-separator"
                                sx={{ display: "flex", justifyContent: "center", my: 0.25 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 1.25,
                                    py: 0.25,
                                    borderRadius: 4,
                                    bgcolor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.08)"
                                        : "rgba(0,0,0,0.06)",
                                    color: "text.secondary",
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {formatMessageDayLabel(createdAt, { isEn })}
                                </Typography>
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "flex-end",
                                justifyContent: isMe ? "flex-end" : "flex-start",
                                gap: { xs: 0.75, sm: 1 },
                                minWidth: 0,
                                width: "100%",
                              }}
                            >
                              {/* Left Avatar for other user */}
                              {!isMe && (
                                <Avatar
                                  src={senderAvatar}
                                  onClick={(e) => handleAvatarClick(e, activeChatUser)}
                                  sx={{
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    fontSize: "0.8rem",
                                    bgcolor: "primary.main",
                                    fontWeight: 800,
                                    cursor: "pointer",
                                    mb: 0.5,
                                    flexShrink: 0,
                                    display: { xs: "none", sm: "flex" },
                                  }}
                                >
                                  {senderInitial}
                                </Avatar>
                              )}

                              {/* Message Content Container */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: isMe ? "flex-end" : "flex-start",
                                  maxWidth: { xs: "80%", sm: "75%" },
                                  minWidth: 0,
                                  width: "fit-content",
                                  flex: "0 1 auto",
                                }}
                              >
                                {/* Attached Scripture Quote Card */}
                                {m.verseQuote && (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1,
                                      px: 1.5,
                                      mb: 0.75,
                                      borderRadius: 2.5,
                                      bgcolor: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(25, 118, 210, 0.25)"
                                          : "#e3f2fd",
                                      border: "1px solid",
                                      borderColor: "primary.main",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                      <MenuBookIcon sx={{ fontSize: 16, mt: 0.15, flexShrink: 0 }} color="primary" />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 800,
                                          color: "primary.main",
                                          wordBreak: "break-word",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        引用经文：{sanitizeQuoteDisplay(m.verseQuote)}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )}

                                {/* Main Text Bubble */}
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: { xs: 1, sm: 1.25 },
                                    px: { xs: 1.5, sm: 2 },
                                    maxWidth: "100%",
                                    borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                                    background: isMe
                                      ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                                      : (theme) =>
                                          theme.palette.mode === "dark"
                                            ? "rgba(255, 255, 255, 0.08)"
                                            : "#ffffff",
                                    color: isMe ? "#ffffff" : "text.primary",
                                    border: isMe
                                      ? "none"
                                      : (theme) =>
                                          theme.palette.mode === "dark"
                                            ? "1px solid rgba(255,255,255,0.06)"
                                            : "1px solid rgba(0,0,0,0.06)",
                                    boxShadow: isMe
                                      ? "0 3px 12px rgba(25, 118, 210, 0.28)"
                                      : "0 2px 8px rgba(0,0,0,0.05)",
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      lineHeight: 1.5,
                                      fontSize: "0.92rem",
                                      wordBreak: "break-word",
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {sanitizeQuoteDisplay(m.content)}
                                  </Typography>
                                </Paper>

                                {/* Timestamp */}
                                <Typography
                                  data-testid="message-time"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.35, px: 0.75, fontSize: "0.65rem" }}
                                >
                                  {createdAt ? formatMessageTime(createdAt, { isEn }) : ""}
                                </Typography>
                              </Box>

                              {/* Right Avatar for current user */}
                              {isMe && (
                                <Avatar
                                  src={senderAvatar}
                                  sx={{
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    fontSize: "0.8rem",
                                    bgcolor: "primary.dark",
                                    fontWeight: 800,
                                    mb: 0.5,
                                    flexShrink: 0,
                                    display: { xs: "none", sm: "flex" },
                                  }}
                                >
                                  {senderInitial}
                                </Avatar>
                              )}
                            </Box>
                            </React.Fragment>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </Box>

                    {/* Chat Bottom Input Section */}
                    <Box
                      sx={{
                        p: { xs: 1.25, sm: 1.5 },
                        borderTop: "1px solid",
                        borderColor: "divider",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "background.default" : "#ffffff",
                        flexShrink: 0,
                      }}
                    >
                      {/* Attached Scripture Pill Tag */}
                      {attachedVerse && (
                        <Box
                          data-testid="pending-quote-chip"
                          sx={{
                            mb: 1,
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                            minWidth: 0,
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(25, 118, 210, 0.22)"
                                : "rgba(25, 118, 210, 0.08)",
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 2,
                            border: "1px dashed",
                            borderColor: "primary.main",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0, flex: 1 }}>
                            <MenuBookIcon fontSize="small" color="primary" sx={{ mt: 0.15, flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 800, color: "primary.main", display: "block" }}
                              >
                                已引用经文
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: "primary.main",
                                  display: "block",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {verseQuoteReference(attachedVerse)}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            aria-label="移除引用"
                            onClick={() => setPendingQuote(null)}
                            sx={{ flexShrink: 0 }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}

                      {/* Quick Spiritual Encouragement Chips */}
                      <Box
                        data-testid="chat-quick-replies"
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          mb: 1,
                          overflow: "visible",
                          pb: 0.25,
                        }}
                      >
                        {QUICK_BLESSINGS.map((blessing) => (
                          <Chip
                            key={blessing}
                            label={blessing}
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setMessageInput((prev) => (prev ? `${prev} ${blessing}` : blessing))
                            }
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              cursor: "pointer",
                              flexShrink: 0,
                              "&:hover": { bgcolor: "primary.light", color: "#fff" },
                            }}
                          />
                        ))}
                      </Box>

                      {/* Input Text Field Row */}
                      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                        <Tooltip title="引用当前阅读经文">
                          <IconButton
                            size="small"
                            aria-label="引用当前阅读经文"
                            onClick={handleAttachCurrentReading}
                            color={attachedVerse ? "primary" : "default"}
                            sx={{
                              p: 1,
                              border: "1px solid",
                              borderColor: attachedVerse ? "primary.main" : "divider",
                              borderRadius: "14px",
                            }}
                          >
                            <MenuBookIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <TextField
                          id="social-message-input"
                          name="message"
                          placeholder="输入私信内容，按回车发送..."
                          multiline
                          maxRows={3}
                          size="small"
                          fullWidth
                          value={messageInput}
                          inputProps={{
                            maxLength: 2000,
                            "aria-label": "私信内容",
                            name: "message",
                          }}
                          onChange={(e) => setMessageInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          sx={{
                            minWidth: 0,
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "20px",
                              minHeight: { xs: 44, sm: 40 },
                              fontSize: { xs: "1rem", sm: "0.9rem" },
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.05)"
                                  : "#f8fafc",
                            },
                          }}
                        />

                        <Button
                          variant="contained"
                          color="primary"
                          aria-label="发送私信"
                          disabled={sending || (!messageInput.trim() && !attachedVerse)}
                          onClick={handleSendMessage}
                          sx={{
                            borderRadius: "20px",
                            minWidth: 46,
                            height: 40,
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                          }}
                        >
                          <SendIcon fontSize="small" />
                        </Button>
                      </Box>
                    </Box>
                  </>
                ) : (
                  /* No Chat Selected State */
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      color: "text.secondary",
                      p: 4,
                      textAlign: "center",
                      my: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: "24px",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <ChatIcon sx={{ fontSize: 36, opacity: 0.4 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                      选择对话开启交流
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mb: 2 }}>
                      在左侧选择对话记录，或者切换到主内好友直接发起聊天。
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveTab(1)}
                      sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: "none" }}
                    >
                      查看我的主内好友
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ======================================================== */}
        {/* TAB 1: FRIENDS MANAGEMENT                                */}
        {/* ======================================================== */}
        {activeTab === 1 && (
          <Box sx={{ width: "100%", height: "100%", overflowY: "auto", p: { xs: 2, sm: 3 } }}>
            {/* Search and Add Friends Bar */}
            <Paper
              component="form"
              onSubmit={handleSearch}
              elevation={0}
              sx={{
                p: "6px 12px",
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
                gap: 1,
                border: "1.5px solid",
                borderColor: "primary.light",
                borderRadius: 3,
                mb: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              }}
            >
              <TextField
                id="social-friend-search"
                name="friendSearch"
                placeholder="通过读者昵称或邮箱查找主内好友..."
                variant="standard"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputProps={{ name: "friendSearch" }}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{ px: 1 }}
              />
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={searching || !searchQuery.trim()}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  minWidth: 64,
                  width: "auto",
                  flex: "0 0 auto",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  wordBreak: "keep-all",
                  lineHeight: 1.2,
                  textTransform: "none",
                  fontWeight: 800,
                  boxShadow: "none",
                }}
              >
                {searching ? <CircularProgress size={18} color="inherit" /> : "查找"}
              </Button>
            </Paper>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: "primary.main" }}>
                  搜索结果 ({searchResults.length})
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                  }}
                >
                  <List disablePadding>
                    {searchResults.map((user) => (
                      <ListItem
                        key={user.uid}
                        divider
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                          gap: 1,
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={user.photoURL}
                            onClick={(e) => handleAvatarClick(e, user)}
                            sx={{
                              bgcolor: "primary.main",
                              fontWeight: 800,
                              cursor: "pointer",
                              transition: "transform 0.15s ease",
                              "&:hover": { transform: "scale(1.08)" },
                            }}
                          >
                            {(user.displayName || "读")[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 800, cursor: "pointer", "&:hover": { color: "primary.main" } }}
                              onClick={(e) => handleAvatarClick(e, user)}
                            >
                              {user.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email ? maskEmail(user.email) : "读者"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          {user.friendshipStatus === "accepted" ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ChatIcon />}
                              onClick={() => handleStartChatWithUser(user)}
                              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                            >
                              发私信
                            </Button>
                          ) : user.friendshipStatus === "pending_sent" ? (
                            <Chip
                              icon={<HourglassEmptyIcon sx={{ fontSize: "14px !important" }} />}
                              label="等待确认"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleSendRequest(user.uid)}
                              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                            >
                              添加好友
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="text"
                            onClick={(e) => handleAvatarClick(e, user)}
                            sx={{ borderRadius: 2, textTransform: "none" }}
                          >
                            名片
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {/* Pending Incoming Friend Requests */}
            {friendRequests.incoming.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 800, mb: 1.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Badge badgeContent={friendRequests.incoming.length} color="error" sx={{ mr: 1 }} />
                  待处理的好友申请 ({friendRequests.incoming.length})
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "primary.main",
                    overflow: "hidden",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(25, 118, 210, 0.08)"
                        : "rgba(25, 118, 210, 0.03)",
                  }}
                >
                  <List disablePadding>
                    {friendRequests.incoming.map((req) => (
                      <ListItem
                        key={req.requestId}
                        divider
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                          gap: 1,
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={req.photoURL}
                            onClick={(e) => handleAvatarClick(e, req)}
                            sx={{
                              bgcolor: "primary.main",
                              fontWeight: 800,
                              cursor: "pointer",
                              transition: "transform 0.15s ease",
                              "&:hover": { transform: "scale(1.08)" },
                            }}
                          >
                            {(req.displayName || "读")[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 800, cursor: "pointer" }}
                              onClick={(e) => handleAvatarClick(e, req)}
                            >
                              {req.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              请求添加您为灵修好友 · {new Date(req.requestedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={Boolean(respondingId)}
                            startIcon={
                              respondingId === req.requestId ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <CheckIcon />
                              )
                            }
                            onClick={() => handleRespondRequest(req.requestId, "accept")}
                            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                          >
                            {respondingId === req.requestId ? "处理中" : "同意"}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            disabled={Boolean(respondingId)}
                            onClick={() => handleRespondRequest(req.requestId, "reject")}
                            sx={{ borderRadius: 2, textTransform: "none" }}
                          >
                            忽略
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {/* Outgoing Requests (Awaiting Recipient Approval) */}
            {friendRequests.outgoing.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 800, mb: 1.5 }}>
                  我发出的好友申请 (等待对方验证) ({friendRequests.outgoing.length})
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                  }}
                >
                  <List disablePadding>
                    {friendRequests.outgoing.map((req) => (
                      <ListItem
                        key={req.requestId}
                        divider
                        sx={{ display: "flex", justifyContent: "space-between", py: 1.25, px: 2 }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={req.photoURL}
                            onClick={(e) => handleAvatarClick(e, req)}
                            sx={{
                              bgcolor: "primary.main",
                              fontWeight: 800,
                              cursor: "pointer",
                            }}
                          >
                            {(req.displayName || "读")[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {req.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              申请于 {new Date(req.requestedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          icon={<HourglassEmptyIcon sx={{ fontSize: "14px !important" }} />}
                          label="等待验证"
                          size="small"
                          variant="outlined"
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {/* Accepted Friends List */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  我的主内好友 ({friends.length})
                </Typography>
                <Tooltip title="刷新好友列表">
                  <IconButton size="small" onClick={loadFriendsData}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {friends.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: "center",
                    border: "1.5px dashed",
                    borderColor: "divider",
                    borderRadius: 3,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fafbfc",
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 44, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    暂无好友
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 360, mx: "auto" }}>
                    通过上方搜索昵称，或在社区灵修分享中点击作者头像查看名片并加为好友！双方验证通过后即可在此交流。
                  </Typography>
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                  }}
                >
                  <List disablePadding>
                    {friends.map((f) => (
                      <ListItem
                        key={f.uid}
                        divider
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                          gap: 1,
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={f.photoURL}
                            onClick={(e) => handleAvatarClick(e, f)}
                            sx={{
                              bgcolor: "primary.main",
                              fontWeight: 800,
                              cursor: "pointer",
                              transition: "transform 0.15s ease",
                              "&:hover": { transform: "scale(1.08)" },
                            }}
                          >
                            {(f.displayName || "读")[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 800,
                                  cursor: "pointer",
                                  "&:hover": { color: "primary.main" },
                                }}
                                onClick={(e) => handleAvatarClick(e, f)}
                              >
                                {f.displayName}
                              </Typography>
                              {f.unreadMessages > 0 && (
                                <Chip
                                  label={`${f.unreadMessages} 条新消息`}
                                  size="small"
                                  color="error"
                                  sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              好友建立于：{new Date(f.friendSince).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<ChatIcon />}
                            onClick={() => handleStartChatWithUser(f)}
                            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                          >
                            私信
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleAvatarClick(e, f)}
                            sx={{ borderRadius: 2, textTransform: "none", minWidth: 64 }}
                          >
                            名片
                          </Button>
                          <Tooltip title="解除好友">
                            <IconButton
                              size="small"
                              aria-label="解除好友"
                              onClick={() => handleRemoveFriend(f.uid)}
                              color="error"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
    <QuotePickerDialog
      open={quoteDialogOpen}
      draft={quoteDraft}
      onClose={() => setQuoteDialogOpen(false)}
      onConfirm={handleConfirmQuote}
      onBookChange={(bookIndex) =>
        handleQuoteLocationChange({
          bookIndex,
          chapterNumber:
            bookIndex === quoteDraft.bookIndex ? quoteDraft.chapterNumber : 1,
          verse: bookIndex === quoteDraft.bookIndex ? quoteDraft.verse : 1,
        })
      }
      onChapterChange={(chapterNumber) =>
        handleQuoteLocationChange({
          bookIndex: quoteDraft.bookIndex,
          chapterNumber,
          verse:
            chapterNumber === quoteDraft.chapterNumber ? quoteDraft.verse : 1,
        })
      }
      onVerseChange={(verse) =>
        setQuoteDraft((prev) => ({
          ...prev,
          verse,
          verseText: prev.verseMap[verse] || "",
        }))
      }
    />
    </>
  );
}
